<script setup lang="ts">
import { format as formatDate, parseISO } from "date-fns";
import Tooltip, { type TooltipOptions } from "primevue/tooltip";
import { computed } from "vue";
import { type RouteLocationRaw, useRouter } from "vue-router";
import ArrowUpRight from "@/components/shared/icons/ArrowUpRight.vue";
import EditIcon from "@/components/shared/icons/EditIcon.vue";
import {
  hasMyStudentsToDosEnabled,
  hasTodoAddPermission,
  hasTodoUpdateSubmissionPermission,
} from "@/composables/authorization.js";
import TodoStatusDropdown from "./TodoStatusDropdown.vue";
import type { TodoSubmission } from "./types";
import { SESSION_TODO_EDIT_MESSAGE, STATUS_BADGE_CLASSES, STATUS_DISPLAY_MAP } from "./types";
import { isApplicationAssigned } from "./utils";

interface TodoRowProps {
  todo: TodoSubmission;
  expanded: boolean;
  overdue?: boolean;
  highPriority?: boolean;
  canViewSubmissions?: boolean;
}

interface TodoRowEmits {
  (e: "toggle"): void;
  (e: "edit"): void;
}

const props = withDefaults(defineProps<TodoRowProps>(), {
  overdue: false,
  highPriority: false,
  canViewSubmissions: false,
});

const emit = defineEmits<TodoRowEmits>();

const router = useRouter();

const submissionRoute = computed<RouteLocationRaw | undefined>(() => {
  if (!props.canViewSubmissions || !props.todo.application_id || props.todo.submissions.length === 0) {
    return undefined;
  }
  return {
    name: "my-students-review",
    params: { applicationId: props.todo.application_id, todoId: props.todo.todo_id },
  };
});

const showSubmissionLink = computed<boolean>(() => submissionRoute.value !== undefined);

const displayName = computed<string>(() => props.todo.display_name || props.todo.title);

// Archived is a system-derived display state, not a status: it overrides
// whatever derived status the endpoint reports for the row's badge.
const isArchived = computed<boolean>(() => !!props.todo.archived_at);

const statusDisplayName = computed<string>(() =>
  isArchived.value ? "Archived" : (STATUS_DISPLAY_MAP[props.todo.status] ?? props.todo.status),
);

const badgeClasses = computed<string>(() => {
  const colors = isArchived.value ? undefined : STATUS_BADGE_CLASSES[props.todo.status];
  if (!colors) return "bg-gray-100 text-gray-600";
  return `${colors.bg} ${colors.text}`;
});

const canEditStatus = computed<boolean>(
  () => hasMyStudentsToDosEnabled.value && hasTodoUpdateSubmissionPermission.value && !isArchived.value,
);

const formattedDueDate = computed<string>(() => {
  if (!props.todo.due_date) return "";
  return `Due ${formatDate(parseISO(props.todo.due_date), "MMM d")}`;
});

const fullDueDate = computed<string>(() => {
  if (!props.todo.due_date) return "--";
  return formatDate(parseISO(props.todo.due_date), "MMM d, yyyy");
});

const typeLabel = computed<string>(() => (props.todo.todo_type === "Assigned Task" ? "Task" : "Form"));

const latestSubmission = computed<TodoSubmission["submissions"][number] | undefined>(() => {
  if (!props.todo.submissions.length) return undefined;
  return [...props.todo.submissions].sort((a, b) => {
    const dateA = a.updated_at || a.created_at || "";
    const dateB = b.updated_at || b.created_at || "";
    return dateB.localeCompare(dateA);
  })[0];
});

const submittedDate = computed<string>(() => {
  if (!latestSubmission.value?.created_at) return "--";
  return formatDate(parseISO(latestSubmission.value.created_at), "MMM d, yyyy");
});

const showPriorityPill = computed<boolean>(() => props.highPriority);

const showEditPencil = computed<boolean>(() => hasMyStudentsToDosEnabled.value && hasTodoAddPermission.value);

const canEditTodo = computed<boolean>(() => isApplicationAssigned(props.todo) && !isArchived.value);

const handleClick = (): void => {
  if (submissionRoute.value) {
    router.push(submissionRoute.value);
    return;
  }
  emit("toggle");
};

const handleEditClick = (): void => {
  emit("edit");
};

const vTooltip = Tooltip;

const editDisabledTooltip: TooltipOptions = {
  value: SESSION_TODO_EDIT_MESSAGE,
  pt: {
    root: { class: "absolute !bg-transparent !p-0 !shadow-none" },
    arrow: { class: "!hidden" },
    text: "w-[230px] rounded px-2.5 py-2 bg-indigo-base text-white font-montserrat text-xs font-medium leading-snug shadow-[0_6px_18px_rgba(0,0,0,0.18)]",
  },
};
</script>

<template>
  <div>
    <div
      class="flex items-start justify-between gap-3 px-6 py-3 cursor-pointer hover:bg-teal-light transition-colors"
      @click="handleClick"
    >
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-1.5">
          <span class="text-sm font-semibold text-indigo-base truncate">
            {{ displayName }}
          </span>
          <span
            v-if="showPriorityPill"
            class="inline-flex items-center gap-1 rounded-full bg-error-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-600 whitespace-nowrap"
          >
            <span class="w-1 h-1 rounded-full bg-red-600" />
            High
          </span>
          <span
            v-if="overdue"
            class="inline-flex items-center gap-1 rounded-full bg-error-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-600 whitespace-nowrap"
          >
            <span class="w-1 h-1 rounded-full bg-red-600" />
            Overdue
          </span>
        </div>
        <div class="flex items-center gap-2 mt-0.5 text-xs text-gray-600">
          <span>{{ typeLabel }}</span>
          <span v-if="todo.due_date">·</span>
          <span v-if="todo.due_date">{{ formattedDueDate }}</span>
        </div>
      </div>

      <div class="flex items-center gap-2 pt-0.5">
        <button
          v-if="showEditPencil && canEditTodo"
          type="button"
          class="mr-4 flex items-center text-teal-900 hover:text-indigo-base transition-colors"
          data-testid="edit-todo-button"
          aria-label="Edit to-do"
          @click.stop="handleEditClick"
        >
          <EditIcon class="w-4 h-4" />
        </button>
        <span
          v-else-if="showEditPencil"
          v-tooltip.top="editDisabledTooltip"
          class="mr-4 flex items-center"
          data-testid="edit-todo-disabled"
          @click.stop
        >
          <button
            type="button"
            disabled
            class="flex items-center text-gray-500 cursor-not-allowed"
            aria-label="Edit to-do (unavailable)"
          >
            <EditIcon class="w-4 h-4" />
          </button>
        </span>
        <TodoStatusDropdown
          v-if="canEditStatus"
          :participant-id="todo.participant_id"
          :application-id="todo.application_id"
          :todo-id="todo.todo_id"
          :status="todo.status"
          :permitted-statuses="todo.permitted_statuses"
          @click.stop
        />
        <span
          v-else
          class="rounded-full px-3 py-1 text-xs font-semibold whitespace-nowrap"
          :class="badgeClasses"
        >
          {{ statusDisplayName }}
        </span>
        <ArrowUpRight
          v-if="showSubmissionLink"
          custom-class="w-4 h-4 text-teal-900"
          aria-label="Open submission"
        />
        <span
          v-else
          class="material-symbols-outlined text-lg text-gray-600 transition-transform duration-150 leading-none"
          :class="{ 'rotate-180': expanded }"
        >
          expand_more
        </span>
      </div>
    </div>

    <Transition name="slide">
      <div
        v-if="expanded"
        class="px-6 pb-4 pt-2 bg-gray-50 border-t border-gray-100"
      >
        <div v-if="todo.instructions_html" class="mb-3">
          <span class="text-xs font-semibold text-gray-500">Instructions</span>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="text-sm text-indigo-base mt-0.5 leading-relaxed" v-html="todo.instructions_html" />
        </div>
        <div v-if="latestSubmission?.feedback_html" class="mb-3">
          <span class="text-xs font-semibold text-gray-500">Feedback</span>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div
            class="text-sm mt-0.5 leading-relaxed"
            :class="todo.status === 'Requires a Change' ? 'text-red-600' : 'text-indigo-base'"
            v-html="latestSubmission.feedback_html"
          />
        </div>
        <div class="flex items-center gap-6 mt-2">
          <div>
            <span class="text-xs font-semibold text-gray-500">Due</span>
            <p class="text-sm text-indigo-base">{{ fullDueDate }}</p>
          </div>
          <div v-if="latestSubmission?.created_at">
            <span class="text-xs font-semibold text-gray-500">Submitted</span>
            <p class="text-sm text-indigo-base">{{ submittedDate }}</p>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: max-height 0.2s ease, opacity 0.2s ease;
  overflow: hidden;
  max-height: 400px;
}
.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
